import { Dialog } from "@mui/material";
import { stopPropagation } from "@redotech/react-util/event";
import { memo } from "react";
import { Flex } from "../flex";
import * as css from "./merchant-customizable-modal.module.css";

export type ModalPlacement =
  | "center"
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export const MerchantCustomizableModal = memo(
  function MerchantCustomizableModal({
    open,
    onClose,
    children,
    fullscreen,
    backdropColor,
    ariaLabel,
    placement = "center",
    containerStyle,
    allowBackgroundInteraction = false,
  }: {
    open: boolean;
    onClose: () => void;
    children?: React.ReactNode;
    fullscreen: boolean;
    backdropColor?: string;
    ariaLabel?: string;
    placement?: ModalPlacement;
    containerStyle?: React.CSSProperties;
    allowBackgroundInteraction?: boolean;
  }) {
    const positionStyle = getPlacementStyle(placement);

    return (
      <>
        <style>{`.MuiBackdrop-root{background-color: ${backdropColor};}`}</style>
        <Dialog
          aria-label={ariaLabel}
          className={`${css.modalWrapper} ${fullscreen ? css.fullscreen : ""}`}
          disableEnforceFocus={allowBackgroundInteraction}
          disableScrollLock={allowBackgroundInteraction}
          fullScreen={fullscreen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              onClose();
            }
          }}
          open={open}
          PaperProps={{
            style: {
              ...containerStyle,
              ...positionStyle,
              ...(allowBackgroundInteraction ? { pointerEvents: "auto" } : {}),
            },
          }}
          slotProps={
            allowBackgroundInteraction
              ? {
                  root: { style: { pointerEvents: "none" } },
                  backdrop: { style: { pointerEvents: "none" } },
                }
              : undefined
          }
        >
          <Flex
            className={fullscreen ? css.fullscreen : ""}
            onClick={stopPropagation}
          >
            {children}
          </Flex>
        </Dialog>
      </>
    );
  },
);

function getPlacementStyle(placement: ModalPlacement): React.CSSProperties {
  switch (placement) {
    case "center":
      return { margin: "auto" };
    case "top-left":
      return { position: "fixed", top: 20, left: 20, margin: 0 };
    case "top-center":
      return {
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        margin: 0,
      };
    case "top-right":
      return { position: "fixed", top: 20, right: 20, margin: 0 };
    case "bottom-left":
      return { position: "fixed", bottom: 20, left: 20, margin: 0 };
    case "bottom-center":
      return {
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        margin: 0,
      };
    case "bottom-right":
      return { position: "fixed", bottom: 20, right: 20, margin: 0 };
  }
}
